/**
 * The document status pill, and the L3 spine that goes with it.
 *
 * The pill is the same OBJECT as `components/ui/StagePill` — px-10 py-4,
 * `radius/pill`, a 6px `currentColor` dot, `Label/12` at weight 500 — rendered
 * from a different map. It is a separate component rather than a widened
 * `StagePill` because `StagePill`'s prop is `InquiryStage` and a quote has no
 * stage: making that prop a union of two unrelated enums would let
 * `<StagePill stage="pending_push" />` compile.
 *
 * NO COLOUR AND NO LABEL IS NAMED HERE. Both come from `domain/quotes.ts`, which
 * is the single map and the file the contrast test reads.
 */

import {
  DOC_RAIL_GEOMETRY,
  QUOTE_STATUS_MAP,
  QUOTE_TYPE_LABELS,
  SALES_ORDER_STATUS_MAP,
  docRailClass,
  docToneClasses,
} from '../../domain/quotes';
import type { DocTone, QuoteStatus, SalesOrderStatus } from '../../domain/quotes';
import { cn } from '../../lib/cn';

function StatusPill({
  label,
  tone,
  title,
  className,
}: {
  label: string;
  tone: DocTone;
  title?: string;
  className?: string;
}): React.JSX.Element {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-pill px-2.5 py-1 text-label font-medium',
        docToneClasses(tone),
        className,
      )}
    >
      {/* `bg-current` keeps the dot on the pill's own foreground token — the same
       * reason `StagePill` draws a CSS circle instead of using Figma's exported
       * ellipse asset, which would bake in a pre-AA-nudge hex. */}
      <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-current" />
      {label}
    </span>
  );
}

export function QuoteStatusPill({
  status,
  className,
}: {
  status: QuoteStatus;
  className?: string;
}): React.JSX.Element {
  const descriptor = QUOTE_STATUS_MAP[status];
  return (
    <StatusPill
      label={descriptor.label}
      tone={descriptor.tone}
      title={descriptor.meaning ?? undefined}
      className={className}
    />
  );
}

export function SalesOrderStatusPill({
  status,
  className,
}: {
  status: SalesOrderStatus;
  className?: string;
}): React.JSX.Element {
  const descriptor = SALES_ORDER_STATUS_MAP[status];
  return (
    <StatusPill
      label={descriptor.label}
      tone={descriptor.tone}
      title={descriptor.meaning ?? undefined}
      className={className}
    />
  );
}

/**
 * The 3px rail down the left of a record header.
 *
 * Absolutely positioned and BLEEDING LEFT into the page's existing margin
 * (`-left-[15px]`), exactly as `InquiryRecordPage`'s does — that file records the
 * cost of getting it wrong: an in-flow rail pushed every header line 15px right
 * of the card below it and broke the page's shared left edge. This displaces
 * nothing.
 *
 * `aria-hidden`, because the pill beside it says the status in words. A screen
 * reader announcing "blue" would be reading the decoration.
 */
export function DocRail({ tone }: { tone: DocTone }): React.JSX.Element {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'pointer-events-none absolute inset-y-0 -left-[15px]',
        DOC_RAIL_GEOMETRY,
        docRailClass(tone),
      )}
    />
  );
}

/**
 * The document-type tag (§B1).
 *
 * Marks the EXCEPTION, not the rule. A CRF quote is the overwhelming default, and
 * a tag that appears on every row is a tag nobody reads — so `null` for `crf` is
 * the whole point, not an oversight; the record page states the type in prose
 * either way, so nothing is hidden by the list staying quiet.
 *
 * ===========================================================================
 * ⚠ THE ARMS ARE ENUMERATED BECAUSE THE COMPILER CANNOT DO IT HERE
 * ===========================================================================
 * The prop is `string`, not `QuoteType`, deliberately: this component renders a
 * value that came off the wire and must not crash on one it does not know. The
 * cost of that is real, and migration 47 is what made it real — when `mixed`
 * joined the vocabulary, every OTHER site that treated two values as exhaustive
 * failed to compile (`QUOTE_TYPE_LABELS` at three call sites, and
 * `missingPatchReason`). THIS ONE DID NOT. `type !== 'tally_sales'` swallowed the
 * new value into the same silent `null` that `crf` returns, and a mixed document
 * would have been drawn as an ordinary CRF quote — the one reading that is
 * actively wrong, since a mixed document is MORE of an exception than a Tally
 * Sales one, not less.
 *
 * So the three arms are written out and each says what it renders:
 *   · `tally_sales` → the tag. The original exception.
 *   · `mixed`       → the tag. Same treatment, its own label, for the same reason.
 *   · `crf`         → `null`, the ruling above.
 *   · anything else → `null`, unchanged: an unknown string is a wire the client
 *                     does not understand, and drawing a chip full of a raw enum
 *                     value would be worse than drawing nothing.
 *
 * ⚠ REWRITTEN IN THIS PASS (rule 5). WAS: *"NOTHING PRODUCES A MIXED DOCUMENT
 * YET (the API refuses the value at create; the derivation is the next unit), so
 * the middle arm is unreachable today."* The stated reason is false — the API
 * DERIVES `mixed` (C11/IQ-12b) and it is the ordinary answer for a compliant
 * AO/CD Tally document — and the conclusion is right for a different reason.
 *
 * EVERY arm here is unreached, `tally_sales` included, because NO SURFACE
 * RENDERS `QuoteTypeTag`: the WT3 view-B ruling replaced the table with the
 * worklist, whose row grammar prints the type on every row itself. That is
 * measured and argued in `quote-type-tag.test.tsx`, which is the suite that
 * exists to keep the component honest while it is unrendered. Whether it comes
 * back or goes is a separate decision with its own argument.
 *
 * Neutral by construction: `text-fg-secondary` on `bg-surface`, the pair the
 * contrast suite already measures in both modes. The border is decorative — it
 * separates the tag from the number beside it and carries no meaning. `mixed`
 * takes the SAME tokens on purpose: the tag says "look at this document", and a
 * second colour would be saying something about the outcome, which belongs to
 * the status pill beside it.
 */
export function QuoteTypeTag({ type }: { type: string }): React.JSX.Element | null {
  if (type !== 'tally_sales' && type !== 'mixed') return null;
  return (
    <span className="inline-flex items-center whitespace-nowrap rounded-pill border border-line px-1.5 py-0.5 text-label font-medium text-fg-secondary">
      {QUOTE_TYPE_LABELS[type]}
    </span>
  );
}
